// Metaphysics RAG / KG client + types.
//
// Mirrors the slice of the metaphysics API the /internal/knowledge-graphs page
// needs:  /rag/graph, /rag/labels, /rag/docs, /rag/query, /rag/versions, and
// the /eval/* endpoints used by the inline "KG quality" panel.
//
// Keeps a deliberate split from `metaphysics-pipelines.ts` (admin pipeline
// editor) and `metaphysics.ts` (document-sets + suggestions) so each module
// stays focused. All three share `metaphysicsBaseUrl` + the user's JWT.

import { useMemo } from 'react';

import { useAuth } from '../auth/AuthProvider';

import { metaphysicsBaseUrl } from './metaphysics';

// ── Types ─────────────────────────────────────────────────────────────────

/**
 * Per-pipeline LightRAG role → model override map. Each role is optional;
 * missing roles fall through to the env-level defaults at sync time.
 */
export interface ILightragLlmRoleConfig {
  extract?: string;
  keyword?: string;
  query?: string;
  vlm?: string;
}

/** Slim pipeline shape used by the picker — full type lives in metaphysics-pipelines. */
export interface IKgPipeline {
  id: string;
  name: string;
  /** Per-pipeline LLM role overrides. Empty object means env defaults apply. */
  lightragLlmRoleConfig?: ILightragLlmRoleConfig;
}

/**
 * KG owner discriminator. Every KG-scoped call (versions, graph, docs,
 * summary) needs to know whether it's hitting a pipeline-backed manifest or
 * a documentSet-backed KG version. The two paths point at different
 * metaphysics endpoints but produce the same `IKgVersion` / `IGraphResponse`
 * shapes at the viewer layer (the client normalises documentSet rows into
 * `IKgVersion`).
 */
export type IKgOwner =
  | { kind: 'pipeline'; pipelineId: string }
  | { kind: 'documentSet'; documentSetId: string };

export interface IKgVersion {
  id: string;
  version: number;
  domain: string;
  entityTypes: string[];
  entityExamples?: unknown;
  domainContext: string | null;
  knownEntities?: unknown;
  /** Empty `{}` for documentSet-owned KGs (no artifacts on that path). */
  sourceArtifactVersions: Record<string, number>;
  kgWorkspace: string | null;
  manifestInputTokens: number | null;
  manifestOutputTokens: number | null;
  manifestCostUsd: number | null;
  lightragLlmModel: string | null;
  lightragEmbeddingModel: string | null;
  lightragLlmInputTokens: number | null;
  lightragLlmOutputTokens: number | null;
  lightragEmbeddingTokens: number | null;
  lightragCostUsd: number | null;
  createdAt: string;
}

export interface IRawGraphNode {
  id: string;
  labels?: string[];
  label?: string;
  properties?: Record<string, unknown>;
}

export interface IRawGraphEdge {
  source: string;
  target: string;
  properties?: Record<string, unknown>;
}

export interface IGraphResponse {
  nodes: IRawGraphNode[];
  edges: IRawGraphEdge[];
}

export interface IRawDoc {
  id: string;
  file_path?: string;
  status?: string;
  chunks_count?: number;
  content_length?: number;
  updated_at?: string;
  error_msg?: string;
}

export interface IKgWorkspaceProgress {
  curBatch: number;
  totalBatches: number;
  jobStart: string | null;
}

export interface IDocInfo {
  id: string;
  name: string;
  status: string;
  chunks: number | null;
  contentLength: number | null;
  updatedAt: string | null;
  error: string | null;
}

export interface IDocListResponse {
  documents: IDocInfo[];
  workspaceProgress: IKgWorkspaceProgress | null;
}

/** One persisted chunk as returned by `/rag/docs/:docId/chunks`. */
export interface IDocChunk {
  id: string;
  chunkOrderIndex: number;
  tokens: number;
  content: string;
  filePath: string | null;
}

interface IRawDocChunk {
  id: string;
  chunk_order_index: number;
  tokens: number;
  content: string;
  file_path: string | null;
}

/**
 * RAG-query request body. Exactly one of `pipelineId` / `documentSetId`
 * must be set; the server enforces mutual exclusion and 400s otherwise.
 * Mirrors the same owner-discriminator pattern as `/rag/graph`.
 */
export interface IRagQueryRequest {
  query: string;
  mode: string;
  pipelineId?: string;
  documentSetId?: string;
  kgVersion?: number;
}

export interface IRagQueryResponse {
  answer?: string;
  entities?: string[];
}

export interface IScoreAggregate {
  mean?: number;
  passRate?: number;
}

export interface IEvalRunSummary {
  id: string;
  startedAt?: string | null;
  completedAt?: string | null;
  config?: Record<string, unknown>;
  scoreAggregates?: Record<string, IScoreAggregate>;
  resultCount?: number;
}

export interface ITriggerEvalInput {
  pipelineId: string;
  kgVersion?: number;
  modes: string[];
}

/**
 * Per-entity-type summary of a KG version — count + a few example entity_ids.
 * Backs the histogram in the KG-page info sidebar. Counts are lower bounds
 * when `truncated` is true (the underlying graph exceeded LightRAG's
 * 5000-node bounded-BFS cap).
 */
export interface IKgSummary {
  workspace: string;
  version: number | null;
  totalNodes: number;
  totalEdges: number;
  truncated: boolean;
  entityTypes: Array<{
    type: string;
    count: number;
    examples: string[];
  }>;
}

// ── Client ────────────────────────────────────────────────────────────────

/**
 * Owner-with-KG summary row. Returned by `GET /kg/owners` and used by the
 * KG-picker dropdown on `/internal/knowledge-graphs` to hide entries whose
 * `latestKgVersion` is null (i.e. never had a sync).
 */
export interface IKgOwnerSummary {
  id: string;
  name: string;
  latestKgVersion: number | null;
}

export interface IKgOwnersResponse {
  pipelines: IKgOwnerSummary[];
  documentSets: IKgOwnerSummary[];
}

export interface IKgClient {
  listPipelines: () => Promise<IKgPipeline[]>;
  listOwners: () => Promise<IKgOwnersResponse>;
  listVersions: (owner: IKgOwner) => Promise<IKgVersion[]>;
  getGraph: (owner: IKgOwner, kgVersion: number | null) => Promise<IGraphResponse>;
  listLabels: (owner: IKgOwner, kgVersion: number | null) => Promise<string[]>;
  listDocs: (owner: IKgOwner, kgVersion: number | null) => Promise<IDocListResponse>;
  /** Per-doc chunk view — what LightRAG actually stored after chunking. */
  getDocChunks: (owner: IKgOwner, kgVersion: number | null, docId: string) => Promise<IDocChunk[]>;
  /** Pipeline-only — the documentSet path doesn't have a kg-summary endpoint yet. */
  getKgSummary: (pipelineId: string, kgVersion: number | null) => Promise<IKgSummary>;
  /** Pipeline-only — `/rag/query` is keyed on pipeline + manifest. */
  query: (input: IRagQueryRequest) => Promise<IRagQueryResponse>;
  listEvalRuns: (limit?: number) => Promise<IEvalRunSummary[]>;
  getEvalRun: (id: string) => Promise<IEvalRunSummary>;
  triggerEval: (input: ITriggerEvalInput) => Promise<{ message: string }>;
}

function buildClient(token: string | null): IKgClient {
  async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    const res = await fetch(`${metaphysicsBaseUrl}/${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: 'omit',
    });
    if (!res.ok) {
      let txt = '';
      try {
        txt = await res.text();
      } catch {
        /* ignore */
      }
      throw new Error(`${method} ${path} → ${res.status}${txt ? `: ${txt}` : ''}`);
    }
    return (await res.json()) as T;
  }

  /**
   * Build the owner query-param fragment used by `/rag/*` endpoints. Both
   * sides (pipeline and documentSet) are accepted by ragController.
   */
  function withOwner(owner: IKgOwner, kgVersion: number | null): string {
    const v = kgVersion !== null ? `&kgVersion=${kgVersion}` : '';
    if (owner.kind === 'pipeline') {
      return `pipelineId=${encodeURIComponent(owner.pipelineId)}${v}`;
    }
    return `documentSetId=${encodeURIComponent(owner.documentSetId)}${v}`;
  }

  /**
   * documentSet versions live under a different endpoint and have a slightly
   * different shape (no sourceArtifactVersions). Normalise into `IKgVersion`
   * so the viewer can render uniformly.
   */
  interface IRawDocumentSetKgVersion extends Omit<IKgVersion, 'sourceArtifactVersions'> {
    documentSetVersionId: string;
  }

  function normaliseDocumentSetKgVersion(row: IRawDocumentSetKgVersion): IKgVersion {
    return {
      id: row.id,
      version: row.version,
      domain: row.domain,
      entityTypes: row.entityTypes,
      entityExamples: row.entityExamples,
      domainContext: row.domainContext,
      knownEntities: row.knownEntities,
      sourceArtifactVersions: {}, // empty on the documentSet path by design
      kgWorkspace: row.kgWorkspace,
      manifestInputTokens: row.manifestInputTokens,
      manifestOutputTokens: row.manifestOutputTokens,
      manifestCostUsd: row.manifestCostUsd,
      lightragLlmModel: row.lightragLlmModel,
      lightragEmbeddingModel: row.lightragEmbeddingModel,
      lightragLlmInputTokens: row.lightragLlmInputTokens,
      lightragLlmOutputTokens: row.lightragLlmOutputTokens,
      lightragEmbeddingTokens: row.lightragEmbeddingTokens,
      lightragCostUsd: row.lightragCostUsd,
      createdAt: row.createdAt,
    };
  }

  return {
    listPipelines: () => call<{ items: IKgPipeline[] }>('GET', 'pipeline').then((r) => r.items),

    listOwners: () => call<IKgOwnersResponse>('GET', 'kg/owners'),

    listVersions: async (owner) => {
      if (owner.kind === 'pipeline') {
        const r = await call<{ items: IKgVersion[] }>(
          'GET',
          `rag/versions?pipelineId=${encodeURIComponent(owner.pipelineId)}`,
        );
        return r.items;
      }
      const r = await call<{ items: IRawDocumentSetKgVersion[] }>(
        'GET',
        `document-sets/${encodeURIComponent(owner.documentSetId)}/kg/versions`,
      );
      return r.items.map(normaliseDocumentSetKgVersion);
    },

    getGraph: (owner, kgVersion) =>
      call<IGraphResponse>(
        'GET',
        `rag/graph?label=*&max_depth=3&max_nodes=500&${withOwner(owner, kgVersion)}`,
      ),

    listLabels: async (owner, kgVersion) => {
      const data = await call<unknown>('GET', `rag/labels?${withOwner(owner, kgVersion)}`);
      return Array.isArray(data) ? (data as string[]).slice().sort() : [];
    },

    listDocs: async (owner, kgVersion) => {
      const data = await call<{
        documents?: IRawDoc[];
        workspaceProgress?: {
          curBatch?: number;
          totalBatches?: number;
          jobStart?: string | null;
        } | null;
      }>('GET', `rag/docs?${withOwner(owner, kgVersion)}`);
      const documents = (data.documents ?? []).map((d) => ({
        id: d.id,
        name: d.file_path || d.id,
        status: d.status || 'unknown',
        chunks: d.chunks_count ?? null,
        contentLength: d.content_length ?? null,
        updatedAt: d.updated_at ?? null,
        error: d.error_msg ?? null,
      }));
      const wp = data.workspaceProgress;
      const workspaceProgress =
        wp && (wp.totalBatches ?? 0) > 0
          ? {
              curBatch: wp.curBatch ?? 0,
              totalBatches: wp.totalBatches ?? 0,
              jobStart: wp.jobStart ?? null,
            }
          : null;
      return { documents, workspaceProgress };
    },

    getDocChunks: async (owner, kgVersion, docId) => {
      const data = await call<{ chunks?: IRawDocChunk[] }>(
        'GET',
        `rag/docs/${encodeURIComponent(docId)}/chunks?${withOwner(owner, kgVersion)}`,
      );
      return (data.chunks ?? []).map((c) => ({
        id: c.id,
        chunkOrderIndex: c.chunk_order_index,
        tokens: c.tokens,
        content: c.content,
        filePath: c.file_path ?? null,
      }));
    },

    getKgSummary: (pipelineId, kgVersion) => {
      const v = kgVersion !== null ? `?kgVersion=${kgVersion}` : '';
      return call<IKgSummary>('GET', `pipeline/${encodeURIComponent(pipelineId)}/kg-summary${v}`);
    },

    query: (input) => call<IRagQueryResponse>('POST', 'rag/query', input),

    listEvalRuns: (limit = 50) =>
      call<{ items: IEvalRunSummary[] }>('GET', `eval/runs?limit=${limit}`).then((r) => r.items),
    getEvalRun: (id) => call<IEvalRunSummary>('GET', `eval/runs/${encodeURIComponent(id)}`),
    triggerEval: (input) => call<{ message: string }>('POST', 'eval/trigger', input),
  };
}

/** Hook returning a metaphysics-kg client bound to the current Keycloak token. */
export function useMetaphysicsKg(): IKgClient {
  const { token } = useAuth();
  return useMemo(() => buildClient(token), [token]);
}
